/** Partner console → Team, for a commercial's FLAT team.
 *
 * Mirrors PartnerTeamPage in structure/styling, but a commercial's
 * teammates are all equal — there are no roles. Commercials invite
 * teammates by email (optional name), and can remove anyone but
 * themselves. Commercial-only; the nav hides it for resellers and this
 * guards direct URL access too. */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PartnerPageHeader } from "@/components/PartnerLayout";
import { Spinner } from "@/components/Spinner";
import {
  useCommercialTeam,
  useInviteCommercialTeammate,
  usePartnerMe,
  useRemoveCommercialTeammate,
} from "@/lib/partner";

export default function PartnerCommercialTeamPage() {
  const { t } = useTranslation();
  const me = usePartnerMe();
  const isCommercial = me.data?.kind === "commercial";
  const team = useCommercialTeam(isCommercial);
  const invite = useInviteCommercialTeammate();
  const remove = useRemoveCommercialTeammate();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (me.isLoading) return <Spinner fullscreen label={t("common.loading")} />;
  if (!isCommercial) {
    return (
      <div className="text-sm text-nb-text-2">
        {t("partnerCommercialTeam.commercialOnly")}
      </div>
    );
  }

  const submitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSentTo(null);
    try {
      const res = (await invite.mutateAsync({
        email: email.trim(),
        name: name.trim() || undefined,
      })) as { email?: string };
      setSentTo(res.email ?? email.trim());
      setEmail("");
      setName("");
    } catch (e2) {
      const detail = (e2 as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setErr(detail || t("partnerTeam.inviteError"));
    }
  };

  const data = team.data;

  return (
    <div className="space-y-6">
      <PartnerPageHeader
        title={t("partnerCommercialTeam.title")}
        description={t("partnerCommercialTeam.description")}
      />

      {/* Invite */}
      <section className="rounded-md border border-nb-border bg-nb-surface/30 p-4">
        <h2 className="mb-3 text-sm font-semibold text-nb-text">
          {t("partnerTeam.inviteHeading")}
        </h2>
        <form className="flex flex-wrap items-end gap-2" onSubmit={submitInvite}>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("partnerTeam.emailPlaceholder")}
            className="min-w-[240px] flex-1 rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
            data-testid="invite-email"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("partnerCommercialTeam.namePlaceholder")}
            className="min-w-[180px] rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
            data-testid="invite-name"
          />
          <button
            type="submit"
            className="btn-primary text-sm"
            disabled={invite.isPending || !email.trim()}
            data-testid="invite-submit"
          >
            {invite.isPending
              ? t("partnerTeam.inviting")
              : t("partnerTeam.sendInvite")}
          </button>
        </form>
        {err && (
          <p className="mt-2 text-xs text-cvp-warn" role="alert">
            {err}
          </p>
        )}
        {sentTo && (
          <p className="mt-2 text-xs text-nb-mint" role="status">
            {t("partnerCommercialTeam.inviteSent", { email: sentTo })}
          </p>
        )}
      </section>

      {/* Members */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-nb-text">
          {t("partnerTeam.membersHeading")}
        </h2>
        {team.isLoading ? (
          <div className="text-sm text-nb-text-3">{t("common.loading")}</div>
        ) : (
          <ul className="divide-y divide-nb-border rounded-md border border-nb-border">
            {(data?.members ?? []).map((m) => (
              <li
                key={m.id}
                className="flex flex-wrap items-center justify-between gap-3 p-3"
              >
                <div className="min-w-0">
                  <span className="text-sm text-nb-text">{m.email}</span>
                  {(m.first_name || m.last_name) && (
                    <span className="ml-2 text-xs text-nb-text-3">
                      {m.first_name} {m.last_name}
                    </span>
                  )}
                  {m.is_self && (
                    <span className="ml-2 text-xs text-nb-mint">
                      {t("partnerTeam.you")}
                    </span>
                  )}
                </div>
                {!m.is_self && (
                  <button
                    type="button"
                    className="text-xs text-cvp-warn hover:underline"
                    onClick={() => remove.mutate(m.id)}
                    data-testid={`member-remove-${m.id}`}
                  >
                    {t("partnerTeam.remove")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pending invites */}
      {(data?.pending_invites?.length ?? 0) > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-nb-text">
            {t("partnerTeam.pendingHeading")}
          </h2>
          <ul className="divide-y divide-nb-border rounded-md border border-nb-border">
            {data!.pending_invites.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center justify-between gap-3 p-3 text-sm"
              >
                <span className="text-nb-text-2">{inv.email}</span>
                <span className="text-xs text-nb-text-3">
                  {t("partnerTeam.pending")}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
